// Settings, help, shortcut editor, team presets, diagnostics (E-21) and the performance overlay (E-22).

import type { App } from '../app/app';
import { APP_VERSION, BUILD_DATE } from '../app/app';
import { getLang, setLang, t } from '../i18n';
import { $, $$, esc, openModal, setText, toast } from './dom';
import { actions, keyString } from './keyboard';
import { parsePresetFile } from '../core/presets';
import { exportTeamPreset } from './dock';
import { kvGet, kvSet } from '../app/persist';
import { AI_ANALYSIS_ENABLED } from '../ai/enabled';

export function initModals(app: App): void {
  $$('.modal').forEach((m) =>
    m.addEventListener('click', (e) => {
      if (e.target === m || (e.target as HTMLElement).closest('[data-close]')) m.hidden = true;
    }),
  );
  $('#btnSettings').addEventListener('click', () => {
    updateSettings(app);
    openModal('#settingsModal');
  });
  $('#btnHelp').addEventListener('click', () => {
    buildHelp(app);
    openModal('#helpModal');
  });
  const bind = (sel: string, ev: string, fn: (el: HTMLInputElement & HTMLSelectElement) => void) => {
    const el = $<HTMLInputElement & HTMLSelectElement>(sel);
    el.addEventListener(ev, () => {
      fn(el);
      void kvSet('settings', app.state.settings);
    });
  };
  bind('#setLang', 'change', (el) => {
    const lang = el.value === 'en' ? 'en' : 'zh-TW';
    app.store.settings((s) => (s.language = lang));
    setLang(lang);
  });
  // AI look (F-10.10): the user's own Claude API key, in this browser only (never in the project file).
  const aiKey = $<HTMLInputElement>('#setAiKey');
  aiKey.addEventListener('change', () => void kvSet('aiKey', aiKey.value.trim() || undefined));
  $('#btnAiKeyClear').addEventListener('click', () => {
    aiKey.value = '';
    void kvSet('aiKey', undefined);
  });
  const aiModel = $<HTMLSelectElement>('#setAiModel');
  aiModel.addEventListener('change', () => void kvSet('aiModel', aiModel.value));
  // Paused for now: not shown.
  $('#setAiSec').hidden = !AI_ANALYSIS_ENABLED;
  bind('#setFlickerHz', 'change', (el) => app.store.settings((s) => (s.flickerHz = Number(el.value))));
  bind('#setTimecode', 'change', (el) => app.store.project((p) => (p.timecodeMode = el.value as 'embedded' | 'from-zero'), { undo: false }));
  bind('#setFrameBase', 'change', (el) => app.store.settings((s) => (s.frameNumberBase = Number(el.value) as 0 | 1)));
  bind('#setDegrade', 'change', (el) => app.store.settings((s) => (s.degradeMode = el.value as 'smooth' | 'quality')));
  bind('#setSampling', 'change', (el) => app.store.project((p) => (p.layout.view.sampling = el.value as 'nearest' | 'smooth'), { undo: false }));
  bind('#setCenterMark', 'change', (el) => app.store.settings((s) => (s.zoomCenterMark = el.checked)));
  bind('#setAlignDiff', 'change', (el) => app.store.settings((s) => (s.alignAutoDifference = el.checked)));
  // Videos from links / moonland (F-01.13): a chosen folder, or memory only.
  bind('#setLinkSave', 'change', (el) => app.store.settings((s) => (s.linkSave = el.value === 'none' ? 'none' : undefined)));
  $('#btnLinkFolder').addEventListener('click', async () => {
    await app.chooseLinkFolder();
    updateSettings(app);
  });
  $('#btnClearAll').addEventListener('click', () => {
    for (const id of app.sources.loadedIds()) app.removeSource(id);
    app.store.project((p) => (p.sources = []));
    $('#settingsModal').hidden = true;
  });
  $('#btnDiag').addEventListener('click', async () => {
    const text = diagnostics(app);
    try {
      await navigator.clipboard.writeText(text);
      toast(t('toast.diagCopied'));
    } catch {
      toast(t('toast.diagFailed'));
    }
  });
  $('#btnPerf').addEventListener('click', () => {
    const el = $('#perfOverlay');
    el.hidden = !el.hidden;
    $('#btnPerf').classList.toggle('on', !el.hidden);
  });
  $('#btnExportPreset').addEventListener('click', () => void exportTeamPreset(app));
  $('#btnImportPreset').addEventListener('click', () => $<HTMLInputElement>('#presetInput').click());
  $<HTMLInputElement>('#presetInput').addEventListener('change', async (e) => {
    const input = e.target as HTMLInputElement;
    const f = input.files?.[0];
    input.value = '';
    if (!f) return;
    const p = parsePresetFile(await f.text());
    if (!p) return toast(t('toast.presetInvalid'), 4000);
    await app.importPresets(p.adjustPresets, p.shortcuts);
    void kvSet('settings', app.state.settings);
    toast(t('toast.presetImported', { n: p.adjustPresets.length }));
  });
  $('#btnShortcuts').addEventListener('click', () => {
    buildShortcuts(app);
    openModal('#shortcutsModal');
  });
  $('#shortcutsReset').addEventListener('click', () => {
    app.store.settings((s) => (s.shortcuts = undefined));
    void kvSet('settings', app.state.settings);
    buildShortcuts(app);
  });
  setInterval(() => updatePerf(app), 500);
}

export function updateSettings(app: App): void {
  const s = app.state.settings;
  $<HTMLSelectElement>('#setLang').value = getLang();
  void kvGet<string>('aiKey').then((k) => ($<HTMLInputElement>('#setAiKey').value = k ?? ''));
  void kvGet<string>('aiModel').then((m) => ($<HTMLSelectElement>('#setAiModel').value = m ?? 'claude-sonnet-5-5'));
  $<HTMLSelectElement>('#setFlickerHz').value = String(s.flickerHz);
  $<HTMLSelectElement>('#setTimecode').value = app.project.timecodeMode;
  $<HTMLSelectElement>('#setFrameBase').value = String(s.frameNumberBase);
  $<HTMLSelectElement>('#setDegrade').value = s.degradeMode;
  $<HTMLSelectElement>('#setSampling').value = app.project.layout.view.sampling;
  $<HTMLInputElement>('#setCenterMark').checked = s.zoomCenterMark;
  $<HTMLInputElement>('#setAlignDiff').checked = s.alignAutoDifference;
  const toFolder = s.linkSave !== 'none';
  $<HTMLSelectElement>('#setLinkSave').value = toFolder ? 'folder' : 'none';
  $('#linkFolderRow').hidden = !toFolder;
  void kvGet<FileSystemDirectoryHandle>('linkFolder').then((d) =>
    setText($('#linkFolderInfo'), d ? d.name : t('settings.linkFolderNone')),
  );
  const cv = app.view.canvas;
  setText($('#dprInfo'), t('settings.dpr', { v: (window.devicePixelRatio || 1).toFixed(2), w: cv.width, h: cv.height }));
  setText($('#versionInfo'), t('settings.version', { v: APP_VERSION, d: BUILD_DATE }));
}

function buildHelp(app: App): void {
  const custom = app.state.settings.shortcuts ?? {};
  const kbd = (k: string) => `<kbd>${esc(prettyKey(k))}</kbd>`;
  const rows = actions().map((a) => `<tr><td>${(custom[a.id] ? [custom[a.id]] : a.keys).map(kbd).join('')}</td><td>${esc(t(a.labelKey))}</td></tr>`);
  rows.splice(0, 0, `<tr><td>${['1', '2', '3', '4'].map(kbd).join('')}</td><td>${esc(t('help.k.sources'))}</td></tr>`);
  rows.push(`<tr><td>${kbd('Esc')}</td><td>${esc(t('help.k.esc'))}</td></tr>`);
  $('#helpKeys').innerHTML = rows.join('');
  const list = (prefix: string) => {
    const out: string[] = [];
    for (let i = 1; t(`${prefix}${i}`) !== `${prefix}${i}`; i++) out.push(`<li>${esc(t(prefix + i))}</li>`);
    return out.join('');
  };
  $('#helpMouse').innerHTML = list('help.mouse');
  $('#helpAbout').innerHTML = list('help.about');
}

function buildShortcuts(app: App): void {
  const custom = app.state.settings.shortcuts ?? {};
  const table = $('#shortcutTable');
  table.innerHTML =
    `<tr><th>${esc(t('shortcuts.action'))}</th><th>${esc(t('shortcuts.key'))}</th></tr>` +
    actions()
      .map((a) => `<tr><td>${esc(t(a.labelKey))}</td><td><button class="shortcut-key" data-id="${a.id}">${esc(prettyKey(custom[a.id] ?? a.keys.join(' / ')))}</button></td></tr>`)
      .join('');
  table.onclick = (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('.shortcut-key');
    if (!b) return;
    b.classList.add('recording');
    b.textContent = t('shortcuts.press');
    const onKey = (ev: KeyboardEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      window.removeEventListener('keydown', onKey, true);
      b.classList.remove('recording');
      if (ev.key === 'Escape') {
        buildShortcuts(app);
        return;
      }
      const ks = keyString(ev);
      if (['Ctrl+W', 'Ctrl+T', 'F11', 'Ctrl+N', 'Ctrl+Q'].includes(ks)) {
        toast(t('shortcuts.reserved'));
        buildShortcuts(app);
        return;
      }
      app.store.settings((s) => {
        const next = { ...(s.shortcuts ?? {}) };
        // A key can only belong to one action.
        for (const [k, v] of Object.entries(next)) if (v === ks) delete next[k];
        next[b.dataset.id!] = ks;
        s.shortcuts = next;
      });
      void kvSet('settings', app.state.settings);
      buildShortcuts(app);
    };
    window.addEventListener('keydown', onKey, true);
  };
}

/** E-21: environment and specs only; no video content and no file paths; never sent anywhere. */
export function diagnostics(app: App): string {
  const gl = app.view.gl.gl;
  const dbg = gl.getExtension('WEBGL_debug_renderer_info');
  const renderer = dbg ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : String(gl.getParameter(gl.RENDERER));
  const lines = [
    `AiComp ${APP_VERSION} (${BUILD_DATE})`,
    `UA: ${navigator.userAgent}`,
    `GPU: ${renderer}`,
    `DPR: ${window.devicePixelRatio}`,
    `Float render targets: ${app.view.gl.supportsFloat}`,
  ];
  for (const id of app.sources.loadedIds()) {
    const s = app.sources.source(id)!;
    const c = app.sources.client(id)!;
    const spec = { ...s.spec };
    lines.push(`${id}: ${JSON.stringify(spec)}`);
    lines.push(`${id} mapping=${JSON.stringify(s.frameMapping)} offset=${s.frameOffset} transform=${JSON.stringify(s.spatialTransform)}`);
    if (c.errorLog.length) lines.push(`${id} errors: ${c.errorLog.map((e) => `#${e.index} ${e.detail}`).join('; ')}`);
  }
  for (const [id, e] of app.sources.errors) lines.push(`${id} load error: ${e.errorKey} ${e.detail}`);
  return lines.join('\n');
}

function updatePerf(app: App): void {
  const el = $('#perfOverlay');
  if (el.hidden) return;
  const lines: string[] = [];
  for (const id of app.sources.loadedIds()) {
    const c = app.sources.client(id)!;
    const d = c.decodeTimes;
    const avg = d.length ? d.reduce((a, b) => a + b, 0) / d.length : 0;
    lines.push(`${id}: decode ${avg.toFixed(1)} ms · cache ${c.cachedCount} (${(c.cachedBytes / 1048576).toFixed(0)} MB) · scan ${(c.scanProgress * 100).toFixed(0)}%`);
  }
  lines.push(`dropped ${app.player.dropped} · fps ${fpsMeter(app)}`);
  el.textContent = lines.join('\n');
}

let lastTick = 0;
let lastAt = performance.now();
function fpsMeter(app: App): string {
  const now = performance.now();
  const dt = (now - lastAt) / 1000;
  const df = app.player.tick - lastTick;
  lastAt = now;
  lastTick = app.player.tick;
  return dt > 0 ? (df / dt).toFixed(1) : '—';
}

/** Arrow keys as symbols in help and the shortcut editor. */
export function prettyKey(k: string): string {
  return k.replace(/ArrowLeft/g, '←').replace(/ArrowRight/g, '→').replace(/ArrowUp/g, '↑').replace(/ArrowDown/g, '↓');
}
